import { FC, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { AxiosError } from "axios";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { pl } from "date-fns/locale/pl";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  absencesAbsencesConfigurationWrite,
  absencesOtherEmployeeAbsenceRequestsCreate,
} from "src/utils/RoleBasedSecurity/permissions";
import useBasePath from "src/utils/hooks/useBasePath";
import getYearBoundaries from "src/utils/Company/Absences/getYearBoundaries";
import { transformAbsencePlanToPlannedDays } from "src/utils/Company/Absences/transformAbsencePlan";
import { useFetchEmployeeCalendars } from "src/Pages/Company/Absences/api/useCalendars";
import {
  useFetchAbsencePlan,
  useFetchEmployeeAbsences,
} from "src/Pages/Company/Absences/api/useEmployee";
import useTransformAbsences from "src/utils/hooks/absences/useTransformAbsences";
import { getYearRange } from "src/utils/Company/Absences/checkExcludedWeekDays";
import YearSwitcher from "src/Pages/Company/Absences/YearSwitcher";
import CalendarPreview from "src/Pages/Company/Absences/Calendars/Calendar/CalendarPreview";
import AbsenceRecordsCalendarLegend from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsCalendar/AbsenceRecordsCalendarLegend";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import {
  IAbsenceRequestDay,
  Status,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";

const AbsenceRecordsCalendar: FC = () => {
  const companyId = useCompanyId();
  const { employeeId } = useParams<{ employeeId: string }>();
  const basePath = useBasePath();
  const currentYear = new Date().getFullYear().toString();
  const years = getYearRange(currentYear, 5);
  const canCreateAbsenceRequestForEmployee = usePermission(
    absencesOtherEmployeeAbsenceRequestsCreate
  );
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );

  const [displayYear, setDisplayYear] = useState(currentYear);
  const [displayMonths, setDisplayMonths] = useState(
    getYearBoundaries(currentYear)
  );

  const [transformAbsences] = useTransformAbsences();

  const areAbsencePlansEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ABSENCE_PLANS.toString()
  );

  const {
    data: calendarsData,
    isFetching: isFetchingCalendars,
    isError: isErrorCalendars,
  } = useFetchEmployeeCalendars({
    companyId,
    employeeId,
    years: [displayYear],
  });

  const {
    data: absencesData,
    isFetching: isFetchingAbsences,
    isError: isErrorAbsences,
  } = useFetchEmployeeAbsences({
    employeeId,
    ...displayMonths,
    statuses: [
      Status.WAITING_FOR_APPROVAL,
      Status.ACCEPTED,
      Status.CORRECTION_REQUIRED,
    ],
  });

  const {
    data: absencePlanData,
    isFetching: isFetchingAbsencePlan,
    isError: isErrorAbsencePlan,
    error,
  } = useFetchAbsencePlan(employeeId, displayYear, areAbsencePlansEnabled);

  const [absenceRequestDays] = useMemo<[IAbsenceRequestDay[], Date[]]>(() => {
    if (!absencesData) return [[], []];

    return transformAbsences(absencesData);
  }, [absencesData, transformAbsences]);

  const excludedDays = useMemo(() => {
    if (!calendarsData || isEmpty(calendarsData)) return [];

    return calendarsData.reduce<IExcludedDay[]>(
      (acc, current) => acc.concat(current.excludedDays),
      []
    );
  }, [calendarsData]);

  const plannedDays = useMemo(
    () => transformAbsencePlanToPlannedDays(absencePlanData),
    [absencePlanData]
  );

  const handleYearChange = (year: string) => {
    setDisplayYear(year);
    setDisplayMonths(getYearBoundaries(year));
  };

  const calendars = useMemo(
    () =>
      Array.from(new Array(12)).map((_item, index) => (
        <CalendarPreview
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          year={displayYear}
          month={index}
          absenceRequestDays={absenceRequestDays}
          excludedDays={excludedDays}
          plannedDays={plannedDays}
          disabled={isEmpty(calendarsData)}
        />
      )),
    [absenceRequestDays, calendarsData, displayYear, excludedDays, plannedDays]
  );

  if (
    isErrorCalendars ||
    isErrorAbsences ||
    // if Absence Plan has error 404 error page shouldn't be displayed - plan simply hasn't been created for current year
    (isErrorAbsencePlan && (error as AxiosError).status !== 404)
  )
    return <ErrorDataFetching />;

  return (
    <>
      <Paper sx={{ p: "1.5rem" }} data-testid="absence-records-calendar">
        <Typography variant="h6" component="h2">
          {__("Kalendarz roczny")}
        </Typography>
        <Stack direction="row" justifyContent="space-between" mb="2rem">
          <Box width="200px">
            <YearSwitcher years={years} handleYearChange={handleYearChange} />
          </Box>
          <Box>
            <Button
              variant="contained"
              component={Link}
              size="small"
              to={`${basePath}/absences/absence-request/create/${employeeId}`}
              disabled={!canCreateAbsenceRequestForEmployee}
              sx={{ mr: "0.5rem" }}
              data-testid="new-absence-request-button"
            >
              {__("Złóż wniosek za pracownika")}
            </Button>
            <Button
              variant="contained"
              component={Link}
              size="small"
              to={`${basePath}/absences/calendars-patterns/${employeeId}`}
              disabled={!canWriteAbsencesConfiguration}
              sx={{ ml: "0.5rem" }}
              data-testid="patterns-and-calendars-button"
            >
              {__("Edycja kalendarzy i wzorców")}
            </Button>
          </Box>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="center"
          spacing={4}
          useFlexGap
          position="relative"
          minHeight="500px"
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
            {isFetchingAbsences ||
            isFetchingCalendars ||
            isFetchingAbsencePlan ? (
              <Loader />
            ) : (
              calendars
            )}
          </LocalizationProvider>
        </Stack>
        {absencesData && (
          <AbsenceRecordsCalendarLegend
            absenceGroupInfos={absencesData?.absenceGroupInfos}
          />
        )}
      </Paper>
    </>
  );
};

export default AbsenceRecordsCalendar;
